import React, { useState, useEffect } from 'react'
import ProductOptionSelect from '../fragments/DetailPage/ProductOptionSelect'
import ProductOptionCTA, { getFirstCTA } from '../fragments/DetailPage/ProductOptionCTA'
import NameAndIDsCell from '../components/NameAndIDsCell'
import { createDefaultSelectorOpt } from '../services/PDPService'
import { shouldDisableSort } from '../services/UtilityService'

/**
 * Renders the CTA cell for the product options table.
 * Extracted as a stable component to avoid recreation on every effect run.
 * @param {Object} props
 * @param {Object} props.value - Product option data
 * @param {Map} props.ctaStyleMap - Map of snaps2Id to CTA style type
 */
const CTACell = ({ value, ctaStyleMap }) => {
    const cta = getFirstCTA(value)
    const styleType = ctaStyleMap.get(cta?.snaps2Id) ?? 'primary'
    return <ProductOptionCTA value={value} styleType={styleType} />
}

/**
 * Builds desktop column definitions for the product options (SKUs) table.
 * @param {Object} params
 * @param {Array} params.options - Available options from moreOptions context
 * @param {Object} params.selectedOptions - Currently selected option filters
 * @param {Array} params.productOptions - Full product options list
 * @param {Map} params.ctaStyleMap - Map of snaps2Id to CTA style type
 * @param {Object} params.translations - Translation strings
 * @param {boolean} params.isDesktop - Whether the viewport is desktop
 * @returns {{ columnIdentifiers: Array, desktopCols: Array }}
 */
const useProductOptionsColumns = ({
    options,
    selectedOptions,
    productOptions,
    ctaStyleMap,
    translations,
    isDesktop
}) => {
    const [columnIdentifiers, setColumnIdentifiers] = useState([])
    const [desktopCols, setDesktopCols] = useState([])

    useEffect(() => {
        if (!options?.length) return

        setColumnIdentifiers(
            options.map((option) => {
                return {
                    label: option.label,
                    identifier: option.identifier
                }
            })
        )
    }, [options])

    useEffect(() => {
        if (!isDesktop || !columnIdentifiers.length) return

        /**
         * Check if there is at least one product with CTA Data available (via ctaButtons)
         */
        const hasCTAData = productOptions?.some((product) => !!getFirstCTA(product))
        const isSingleProduct = !productOptions || productOptions.length <= 1
        const disabledSortArrowsClass = isSingleProduct ? 'sps2-pdp_product-options--col-sort-disabled' : ''

        const cols = [
            {
                id: 'column1',
                accessor: 'column1',
                Header: (
                    <span className={disabledSortArrowsClass}>
                        {translations.productNameID}
                    </span>
                ),
                Cell: NameAndIDsCell,
                style: { width: '350px' },
                Filter: '',
                sticky: 'left'
            }
        ]

        columnIdentifiers.forEach((column, index) => {
            const selector = selectedOptions?.options[column.identifier]

            const defaultOpt = createDefaultSelectorOpt(
                translations.all,
                selector?.selectedValue
            )

            const colSelectors = [defaultOpt].concat(selector?.values)

            const hasSingleOrNoValues = !selector?.values || selector?.values.length <= 1
            const disableSortArrows = hasSingleOrNoValues || shouldDisableSort(selector, productOptions, column.identifier)
            const disabledSortArrowsClass = disableSortArrows ? 'sps2-pdp_product-options--col-sort-disabled' : ''

            cols.push({
                id: `column${index + 2}`,
                accessor: `column${index + 2}`,
                style: {
                    width: '200px'
                },
                Header: hasSingleOrNoValues ? (
                    column?.label
                ) : (
                    <div
                        style={{ display: 'contents' }}
                        className={disabledSortArrowsClass}
                    >
                        <ProductOptionSelect
                            column={{ ...column, colSelectors }}
                            index={index}
                        />
                    </div>
                ),
                Filter: '',
                disableSortBy: hasSingleOrNoValues

            })
        })

        if (hasCTAData) {
            cols.push({
                id: `column${columnIdentifiers.length + 2}`,
                accessor: `column${columnIdentifiers.length + 2}`,
                Header: '',
                Cell: ({ value }) => <CTACell value={value} ctaStyleMap={ctaStyleMap} />,
                Filter: '',
                style: { width: '200px' },
                sticky: 'right',
                disableSortBy: true
            })
        }

        setDesktopCols(cols)
    }, [columnIdentifiers, selectedOptions, productOptions, ctaStyleMap, isDesktop])

    return { columnIdentifiers, desktopCols }
}

export default useProductOptionsColumns
